import React, { useEffect, useState } from 'react';
import { Table, Button, Badge, Spinner, Form, Collapse, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../lib/api';

const WidgetList = () => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const fetchWidgets = async () => {
      try {
        const res = await api.get('/admin/widgets/');
        if (!Array.isArray(res.data)) throw new Error('Unexpected response format');
        setWidgets(res.data);
      } catch (err) {
        console.error(err);
        setError('Failed to load widgets');
      } finally {
        setLoading(false);
      }
    };
    fetchWidgets();
  }, []);

  const copyScript = (widgetToken) => {
    const code = `<script src="https://yourdomain.com/widget.js" data-token="${widgetToken}" defer></script>`;
    navigator.clipboard.writeText(code);
    setCopied(widgetToken);
    setTimeout(() => setCopied(null), 1500);
  };

  const toggleWidget = async (id) => {
    try {
      await api.post(`/admin/widgets/toggle/${id}/`);
      setWidgets((prev) =>
        prev.map((w) => (w.id === id ? { ...w, active: !w.active } : w))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const deleteWidget = async (id) => {
    if (!window.confirm('Delete this widget?')) return;
    try {
      await api.delete(`/admin/widgets/delete/${id}/`);
      setWidgets((prev) => prev.filter((w) => w.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <Spinner animation="border" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <>
      <Table striped bordered hover responsive className="shadow-sm mt-3">
        <thead>
          <tr>
            <th>Site</th>
            <th>Token</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {widgets.map((widget) => (
            <React.Fragment key={widget.id}>
              <tr>
                <td>{widget.site}</td>
                <td><code>{widget.token}</code></td>
                <td>
                  {widget.active ? (
                    <Badge bg="success">Active</Badge>
                  ) : (
                    <Badge bg="secondary">Disabled</Badge>
                  )}
                </td>
                <td>{new Date(widget.created_at).toLocaleString()}</td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => copyScript(widget.token)}
                    className="me-1"
                    style={{ cursor: 'pointer' }}
                  >
                    📋 {copied === widget.token ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-warning"
                    onClick={() => toggleWidget(widget.id)}
                    className="me-1"
                    style={{ cursor: 'pointer' }}
                  >
                    {widget.active ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-info"
                    onClick={() => setExpanded(expanded === widget.id ? null : widget.id)}
                    className="me-1"
                    style={{ cursor: 'pointer' }}
                  >
                    {expanded === widget.id ? 'Hide Preview' : 'Preview'}
                  </Button>
                  <Link
                    to={`/widgets/${widget.token}/analytics`}
                    className="btn btn-sm btn-outline-success me-1"
                    style={{ cursor: 'pointer' }}
                  >
                    Analytics
                  </Link>
                  <Link
                    to={`/widgets/${widget.token}/chats`}
                    className="btn btn-sm btn-outline-dark me-1"
                    style={{ cursor: 'pointer' }}
                  >
                    Chats
                  </Link>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => deleteWidget(widget.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    Delete
                  </Button>
                </td>
              </tr>

              <tr>
                <td colSpan="5" style={{ padding: 0, borderTop: 'none' }}>
                  <Collapse in={expanded === widget.id}>
                    <div className="p-3 bg-light">
                      <strong>Embed Script:</strong>
                      <Form.Control
                        as="textarea"
                        value={`<script src="https://yourdomain.com/widget.js" data-token="${widget.token}" defer></script>`}
                        readOnly
                        rows={2}
                        className="mb-3 mt-2"
                      />

                      <div>
                        <strong>Live Preview:</strong>
                        <div className="border mt-2" style={{ height: 400 }}>
                          <iframe
                            src={`https://yourdomain.com/chat-widget?token=${widget.token}`}
                            title="Widget Preview"
                            width="100%"
                            height="100%"
                            frameBorder="0"
                          />
                        </div>
                      </div>
                    </div>
                  </Collapse>
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default WidgetList;
